import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { Box, IconButton, Slider, Typography } from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import { colors, radii } from "../../theme/designTokens";

const formatTime = (sec) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

export default function CustomVideoPlayer({ url, height = 360 }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const hideTimer = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const wake = () => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      setShowControls(false);
    }, 2500);
  };

  useEffect(() => {
    if (playing) wake();
    else {
      clearTimeout(hideTimer.current);
      setShowControls(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const togglePlay = () => {
    setStarted(true);
    setPlaying((p) => !p);
  };

  const handleProgress = (state) => {
    if (!seeking) setPlayed(state.played);
  };

  const handleSeekChange = (e, value) => {
    setSeeking(true);
    setPlayed(value / 100);
  };

  const handleSeekCommit = (e, value) => {
    setSeeking(false);
    if (playerRef.current) playerRef.current.seekTo(value / 100);
  };

  const handleFullscreen = () => {
    if (containerRef.current && containerRef.current.requestFullscreen) {
      containerRef.current.requestFullscreen();
    }
  };

  return (
    <Box
      ref={containerRef}
      onMouseMove={wake}
      sx={{
        position: "relative",
        width: "100%",
        height,
        borderRadius: radii.lg,
        overflow: "hidden",
        bgcolor: "#0B1418",
        "& video": { outline: "none" },
      }}
    >
      <Box
        onClick={togglePlay}
        sx={{ position: "absolute", inset: 0, cursor: "pointer" }}
      >
        <ReactPlayer
          ref={playerRef}
          url={url}
          playing={playing}
          muted={muted}
          controls={false}
          width="100%"
          height="100%"
          onProgress={handleProgress}
          onDuration={setDuration}
          onEnded={() => setPlaying(false)}
          config={{
            youtube: {
              playerVars: { modestbranding: 1, rel: 0, showinfo: 0 },
            },
            vimeo: { playerOptions: { controls: false, byline: false, title: false } },
          }}
        />
      </Box>

      {!playing && (
        <Box
          onClick={togglePlay}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: started ? "rgba(11,20,24,0.25)" : "rgba(11,20,24,0.35)",
            cursor: "pointer",
          }}
        >
          <IconButton
            sx={{
              bgcolor: colors.accent,
              color: "#fff",
              width: 64,
              height: 64,
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
              "&:hover": { bgcolor: colors.accentDark },
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: 38 }} />
          </IconButton>
        </Box>
      )}

      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
          opacity: showControls || !playing ? 1 : 0,
          transition: "opacity 0.25s",
          pointerEvents: showControls || !playing ? "auto" : "none",
        }}
      >
        <IconButton size="small" onClick={togglePlay} sx={{ color: "#fff" }}>
          {playing ? (
            <PauseRoundedIcon fontSize="small" />
          ) : (
            <PlayArrowRoundedIcon fontSize="small" />
          )}
        </IconButton>
        <Typography variant="caption" sx={{ color: "#fff", minWidth: 34 }}>
          {formatTime(played * duration)}
        </Typography>
        <Slider
          size="small"
          value={played * 100}
          onChange={handleSeekChange}
          onChangeCommitted={handleSeekCommit}
          sx={{
            color: colors.accent,
            mx: 0.5,
            "& .MuiSlider-thumb": {
              width: 12,
              height: 12,
              boxShadow: "none",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 6px rgba(232,98,46,0.16)",
              },
            },
            "& .MuiSlider-rail": { opacity: 0.3 },
          }}
        />
        <Typography variant="caption" sx={{ color: "#fff", minWidth: 34 }}>
          {formatTime(duration)}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setMuted((m) => !m)}
          sx={{ color: "#fff" }}
        >
          {muted ? (
            <VolumeOffRoundedIcon fontSize="small" />
          ) : (
            <VolumeUpRoundedIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton size="small" onClick={handleFullscreen} sx={{ color: "#fff" }}>
          <FullscreenRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
